/* ZYMA product row — <zyma-product-row> (sections/zyma-home-product-edit.liquid).
   Native scroll-snap row with page-by-page arrows and optional filter tabs.
   Custom element lifecycle covers shopify:section:load/unload; tab blocks respond to shopify:block:select. */
(() => {
  'use strict';

  if (customElements.get('zyma-product-row')) return;

  class ZymaProductRow extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.track = this.querySelector('[data-products-track]');
      if (!this.track) return;
      this.items = [...this.querySelectorAll('[data-products-item]')];
      this.tabs = [...this.querySelectorAll('[data-products-tab]')];
      this.arrows = this.querySelector('[data-products-arrows]');
      this.prev = this.querySelector('[data-products-prev]');
      this.next = this.querySelector('[data-products-next]');
      this.empty = this.querySelector('[data-products-empty]');
      this.panel = this.querySelector('[data-products-panel]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.prev?.addEventListener('click', () => this.page(-1), { signal });
      this.next?.addEventListener('click', () => this.page(1), { signal });
      this.track.addEventListener('scroll', () => this.update(), { passive: true, signal });

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(tab), { signal });
        tab.addEventListener('keydown', (event) => this.onTabKeydown(event, index), { signal });
      });

      this.progress = this.querySelector('[data-products-progress]');

      document.addEventListener('shopify:block:select', (event) => {
        if (this.tabs.includes(event.target)) {
          this.select(event.target);
          return;
        }
        const item = this.items.find((candidate) => candidate === event.target || candidate.contains(event.target));
        if (item) this.reveal(item);
      }, { signal });

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(() => {
          this.update();
          this.measureChrome();
        });
        observer.observe(this.track);
        observer.observe(this);
        signal.addEventListener('abort', () => observer.disconnect());
      }

      this.update();
      this.measureChrome();
    }

    // Everything in the row except card images, plus breathing room. The CSS caps image height with
    // 100dvh - header - this value, so the heading through the card bottoms always fits one screen.
    measureChrome() {
      const media = this.querySelector('[data-products-item]:not([hidden]) .zyma-product-card__media');
      if (!media) return;
      const chrome = Math.ceil(this.getBoundingClientRect().height - media.getBoundingClientRect().height + 48);
      if (chrome === this.chrome) return;
      this.chrome = chrome;
      this.style.setProperty('--products-chrome', `${chrome}px`);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    get isRtl() {
      return getComputedStyle(this).direction === 'rtl';
    }

    visibleItems() {
      return this.items.filter((item) => !item.hidden);
    }

    page(direction) {
      const first = this.visibleItems()[0];
      if (!first) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = first.getBoundingClientRect().width + gap;
      const perPage = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
      this.track.scrollBy({
        left: direction * (this.isRtl ? -1 : 1) * step * perPage,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    // Theme editor: scroll a selected card block into view.
    reveal(item) {
      const padding = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
      const itemRect = item.getBoundingClientRect();
      const trackRect = this.track.getBoundingClientRect();
      // Align the card to the inline-start edge: the right edge in RTL.
      const offset = this.isRtl ? itemRect.right - trackRect.right + padding : itemRect.left - trackRect.left - padding;
      this.track.scrollTo({ left: this.track.scrollLeft + offset, behavior: 'instant' });
      this.update();
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const position = Math.abs(this.track.scrollLeft);
      const overflow = max > 4;

      if (this.arrows) this.arrows.hidden = !overflow;
      if (this.prev) this.prev.disabled = !overflow || position <= 4;
      if (this.next) this.next.disabled = !overflow || position >= max - 4;

      if (this.progress) {
        this.progress.hidden = !overflow;
        this.style.setProperty('--row-progress-size', String(Math.min(1, this.track.clientWidth / (this.track.scrollWidth || 1))));
        this.style.setProperty('--row-progress-pos', String(overflow ? Math.min(1, position / max) : 0));
      }
    }

    select(tab, { focus = false } = {}) {
      const key = tab.dataset.productsTab;
      this.tabs.forEach((control) => {
        const active = control === tab;
        control.setAttribute('aria-selected', String(active));
        control.tabIndex = active ? 0 : -1;
      });
      if (focus) tab.focus();

      this.items.forEach((item) => {
        const groups = (item.dataset.groups || '').split(/\s+/);
        item.hidden = key !== 'all' && !groups.includes(key);
      });
      if (this.empty) this.empty.hidden = this.visibleItems().length > 0;
      if (this.panel && tab.id) this.panel.setAttribute('aria-labelledby', tab.id);

      this.track.scrollTo({ left: 0, behavior: 'auto' });
      this.update();
    }

    onTabKeydown(event, index) {
      const count = this.tabs.length;
      const forward = this.isRtl ? -1 : 1;
      let next = null;
      if (event.key === 'ArrowRight') next = (index + forward + count) % count;
      if (event.key === 'ArrowLeft') next = (index - forward + count) % count;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = count - 1;
      if (next === null) return;
      event.preventDefault();
      this.select(this.tabs[next], { focus: true });
    }
  }

  customElements.define('zyma-product-row', ZymaProductRow);
})();
